import { deflate, inflate } from 'pako';
import * as Y from 'yjs';

import { CURRENT_MIGRATION_VERSION } from 'src/reducers/migrations/constants';
import { InitialData, migrate } from 'src/reducers/migrations/migrate';
import { Project } from 'src/store/state/projects';
import {
  HEADER_MIGRATION_VERSION,
  HEADER_PROJECT_ID,
  SCBF_MAGIC,
} from 'src/utils/collaboration/scbf/constants';
import { decodeSCBF } from 'src/utils/collaboration/scbf/decode';
import { encodeSCBF } from 'src/utils/collaboration/scbf/encode';
import { projectToYDoc } from 'src/utils/collaboration/scbf/projectToYDoc';
import { yDocToProject } from 'src/utils/collaboration/scbf/yDocToProject';
import { createProjectExportState } from 'src/utils/exportProject/createProjectExportState';
import {
  base64ToBinary,
  base64ToUint8Array,
  binaryToBase64,
  binaryToUrlBase64,
  urlBase64ToBinary,
  urlToUint8Array,
} from 'src/utils/exportProject/urlConversion';

/** Check whether a raw byte array starts with the SCBF "STAR" magic. */
const isSCBFBinary = (data: Uint8Array): boolean => {
  if (data.length < SCBF_MAGIC.length) {
    return false;
  }
  for (let i = 0; i < SCBF_MAGIC.length; i += 1) {
    if (data[i] !== SCBF_MAGIC[i]) {
      return false;
    }
  }
  return true;
};

// ─── SCBF decode helpers ──────────────────────────────────────────────────────

/**
 * Decode an SCBF binary buffer into a `Partial<ReduxState>`.
 */
const decodeSCBFToState = (binary: Uint8Array) => {
  const { headers, documentPayload } = decodeSCBF(binary);

  // Create Y.Doc and apply update
  const doc = new Y.Doc();
  Y.applyUpdate(doc, documentPayload);
  const project = yDocToProject(doc);

  // Read migration version from header
  const migrationVersionStr = headers[HEADER_MIGRATION_VERSION];
  const migrationVersion = migrationVersionStr
    ? parseInt(migrationVersionStr, 10)
    : CURRENT_MIGRATION_VERSION;

  // Wrap in the envelope expected by migrate()
  const { state, version: envVersion } = createProjectExportState(
    project,
    migrationVersion
  );
  return migrate(state, envVersion);
};

// ─── Unified cloud functions ──────────────────────────────────────────────────

/**
 * Decode cloud project content that may be in either legacy or SCBF format.
 * Content is always a standard (non-URL-safe) base64 string from the portal SDK.
 */
export const decodeCloudContent = (content: string) => {
  // Compressed SCBF (binary-safe base64 → deflate → SCBF binary)
  try {
    const binary = base64ToBinary(content);
    const inflated = inflate(binary);
    if (isSCBFBinary(inflated)) {
      return decodeSCBFToState(inflated);
    }
  } catch {
    // Not compressed SCBF — fall through to legacy
  }

  // Legacy pako-compressed JSON format (UTF-8 aware base64)
  const u8 = base64ToUint8Array(content);
  const uncompressed = inflate(u8, { to: 'string' });
  const result = JSON.parse(uncompressed || '{}');

  const isValid = result?.state?.projects && result?.version;
  if (!isValid) {
    throw new Error('Project is invalid.');
  }

  const { state, version } = result as InitialData;
  return migrate(state, version);
};

/**
 * Encode a Project as SCBF for cloud storage, returned as a standard
 * base64 string for transport via the portal SDK.
 */
export const encodeCloudContent = (
  project: Project,
  version: number
): string | null => {
  try {
    const doc = projectToYDoc(project);
    const stateUpdate = Y.encodeStateAsUpdate(doc);

    const scbfBuffer = encodeSCBF({
      documentPayload: stateUpdate,
      headers: {
        [HEADER_MIGRATION_VERSION]: String(version),
        [HEADER_PROJECT_ID]: project.id,
      },
    });

    const compressed = deflate(scbfBuffer);
    return binaryToBase64(compressed);
  } catch (err) {
    console.error('error in encodeCloudContent:', err);
    return null;
  }
};

// ─── Unified local (file export/import) functions ─────────────────────────────

/**
 * Decode a local project file that may be in either legacy or SCBF format.
 * Local files use URL-safe base64.
 */
export const decodeLocalContent = (encoded: string) => {
  // Compressed SCBF (binary-safe URL base64 → deflate → SCBF binary)
  try {
    const binary = urlBase64ToBinary(encoded);
    const inflated = inflate(binary);
    if (isSCBFBinary(inflated)) {
      return decodeSCBFToState(inflated);
    }
  } catch {
    // Not compressed SCBF — fall through to legacy
  }

  // Legacy pako-compressed JSON format (UTF-8 aware base64)
  const u8 = urlToUint8Array(encoded);
  const uncompressed = inflate(u8, { to: 'string' });
  const result = JSON.parse(uncompressed || '{}');

  const isValid = result?.state?.projects && result?.version;
  if (!isValid) {
    throw new Error('Project is invalid.');
  }

  const { state, version } = result as InitialData;
  return migrate(state, version);
};

/**
 * Encode a Project as SCBF for local file export, returned as a URL-safe
 * base64 string.
 */
export const encodeLocalContent = (
  project: Project,
  version: number
): string | null => {
  try {
    const doc = projectToYDoc(project);
    const stateUpdate = Y.encodeStateAsUpdate(doc);

    const scbfBuffer = encodeSCBF({
      documentPayload: stateUpdate,
      headers: {
        [HEADER_MIGRATION_VERSION]: String(version),
        [HEADER_PROJECT_ID]: project.id,
      },
    });

    const compressed = deflate(scbfBuffer);
    return binaryToUrlBase64(compressed);
  } catch (err) {
    console.error('error in encodeLocalContent:', err);
    return null;
  }
};
